import { AxiosError } from 'axios';

import { hsmBackendClient } from './client';
import {
  BackendCompany,
  BackendWebsite,
  Channel,
  ChannelProvider,
  Company,
  IControllerResponse,
} from './types';

export class CompanyNotFoundError extends Error {
  constructor(public readonly companyId: string) {
    super(`Company not found: ${companyId}`);
    this.name = 'CompanyNotFoundError';
  }
}

/**
 * Maps the Kori channel `type` to the HSM provider **capability**. Known even
 * when the channel has no HSM line yet, so it can drive the alta modal mode.
 * Anything that is not a WhatsApp channel → `null` ("Sin proveedor HSM").
 */
export function mapTypeToProvider(type?: string): ChannelProvider | null {
  if (type === 'WHATSAPP_API') {
    return 'gupshup';
  }
  if (type === 'WHATSAPP_BUSINESS') {
    return 'meta_cloud';
  }
  return null;
}

/**
 * Whether a channel is a WhatsApp/HSM-capable channel. Derives from `provider`
 * (the capability mapped from the Kori `type` in {@link mapTypeToProvider}),
 * which is the single source of truth for "what counts as WhatsApp" — so it
 * stays correct if new WhatsApp types are added and never hides a channel just
 * because the raw `channelType` is absent.
 */
export function isWhatsAppChannel(channel: Channel): boolean {
  return channel.provider != null;
}

function toChannel(website: BackendWebsite): Channel {
  return {
    channelId: website.id,
    name: website.title,
    phone: website.phone ?? website.whatsappNumber,
    channelType: website.type,
    whatsappNumber: website.whatsappNumber,
    provider: mapTypeToProvider(website.type),
    hsmLineStatus: website.hsmLineStatus ?? null,
  };
}

export async function getCompany(companyId: string): Promise<Company> {
  try {
    const response = await hsmBackendClient.get<
      IControllerResponse<BackendCompany>
    >(`/company/${encodeURIComponent(companyId)}`);

    if (!response.data.result) {
      throw new CompanyNotFoundError(companyId);
    }

    const { websites, ...company } = response.data.result;

    return {
      ...company,
      channels: (websites ?? []).map(toChannel),
    };
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      throw new CompanyNotFoundError(companyId);
    }
    throw error;
  }
}
